import { Import, ImportRow } from "@/types";
import { useMemo } from "react";
import { Show } from "@/components/core";

interface SourceViewerProps {
  currentImport?: Import;
  row?: ImportRow;
}

interface ParsedError {
  Message: string;
  FromColumnName?: string;
  ToColumnName?: string;
  RowNumber?: number;
  ColumnNumber?: number;
}

export const SourceViewer = (props: SourceViewerProps) => {
  const { currentImport, row } = props;

  const json = useMemo(() => {
    try {
      if (!row?.data?.source || !currentImport?.headerRow) {
        return undefined;
      }

      const headers = currentImport.headerRow
        .split(",")
        .map((x: string) => x.replace('"', ""));
      const columns = row.data.source
        .split(",")
        .map((x: string) => x.replace('"', ""));

      const data = headers.reduce((acc, header, index) => {
        acc[header] = columns[index];
        return acc;
      }, {} as Record<string, any>);

      return data;
    } catch (e) {
      console.error(e);
    }

    return undefined;
  }, [row, currentImport]);

  const errors = useMemo(() => {
    try {
      // Check for error data in the row
      const errorData = row?.data?.error || row?.errorMessage || (row as any)?.error;
      if (!errorData) {
        return undefined;
      }

      const parsedErrors = JSON.parse(errorData) as ParsedError[];
      return parsedErrors;
    } catch (e) {
      console.error("Failed to parse error:", e);
      return undefined;
    }
  }, [row]);

  const formatErrorMessage = (message: string) => {
    // Check if message contains placeholder format ##KEY VALUE##
    const placeholderMatch = message.match(/##([A-Z-]+)\s*([^#]*)##/);
    
    if (placeholderMatch) {
      const [, errorKey, value] = placeholderMatch;
      const humanReadableKey = errorKey
        .split('-')
        .map(word => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
      
      return {
        type: 'handled',
        key: humanReadableKey,
        value: value.trim(),
        original: message
      };
    }

    return {
      type: 'unexpected',
      message: message,
      original: message
    };
  };

  return (
    <div>
      <div className="h-[57px] border-b mb-1 pt-[15px] px-3 font-bold">
        Row Source
      </div>
      <div className="overflow-y-auto h-[538px]">
        <Show if={Boolean(errors && errors.length > 0)}>
          <div className="p-3 mb-4 border-l-4 border-red-500 bg-red-50">
            <div className="font-semibold text-red-700 mb-2">Row Errors:</div>
            {errors?.map((error, index) => {
              const formattedError = formatErrorMessage(error.Message);
              
              return (
                <div key={index} className="mb-3 last:mb-0">
                  {formattedError.type === 'handled' ? (
                    <div>
                      <div className="text-red-600 font-medium">
                        {formattedError.key}
                        {formattedError.value && (
                          <span className="ml-2 px-2 py-1 bg-red-100 rounded text-sm">
                            {formattedError.value}
                          </span>
                        )}
                      </div>
                      {error.FromColumnName && (
                        <div className="text-sm text-red-500 mt-1">
                          Column: {error.FromColumnName}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div className="text-red-600 font-medium">
                        Unexpected Error
                      </div>
                      <div className="text-sm text-red-500 mt-1">
                        {formattedError.message}
                      </div>
                      {error.FromColumnName && (
                        <div className="text-sm text-red-500 mt-1">
                          Column: {error.FromColumnName}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Show>
        
        <Show if={json !== undefined}>
          <pre className="text-xs p-3 bg-gray-50 dark:bg-gray-900 rounded overflow-x-auto">
            {JSON.stringify(json, null, 2)}
          </pre>
        </Show>
      </div>
    </div>
  );
};
